import { useCallback, useEffect, useRef, useState } from 'react';

interface UseRealizationImageDraftParams {
  currentImageUrl: string | null;
  resetKey: string | number | null | undefined;
}

interface UseRealizationImageDraftResult {
  selectedFile: File | null;
  selectedPreviewUrl: string | null;
  removeCurrentImage: boolean;
  displayedImageUrl: string | null;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: (fileInput: HTMLInputElement | null) => void;
  resetDraft: (fileInput: HTMLInputElement | null) => void;
}

export const useRealizationImageDraft = ({
  currentImageUrl,
  resetKey,
}: UseRealizationImageDraftParams): UseRealizationImageDraftResult => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState<string | null>(null);
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    previewUrlRef.current = selectedPreviewUrl;
  }, [selectedPreviewUrl]);

  const clearPreviewUrl = useCallback((previewUrl: string | null) => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, []);

  const resetDraft = useCallback((fileInput: HTMLInputElement | null) => {
    setSelectedFile(null);
    setRemoveCurrentImage(false);
    setSelectedPreviewUrl((currentPreviewUrl) => {
      clearPreviewUrl(currentPreviewUrl);
      return null;
    });

    if (fileInput) {
      fileInput.value = '';
    }
  }, [clearPreviewUrl]);

  useEffect(() => {
    resetDraft(null);
  }, [resetDraft, resetKey]);

  useEffect(() => {
    return () => {
      clearPreviewUrl(previewUrlRef.current);
    };
  }, [clearPreviewUrl]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] || null;

    setSelectedPreviewUrl((currentPreviewUrl) => {
      clearPreviewUrl(currentPreviewUrl);
      return nextFile ? URL.createObjectURL(nextFile) : null;
    });

    setSelectedFile(nextFile);

    if (nextFile) {
      setRemoveCurrentImage(false);
    }
  }, [clearPreviewUrl]);

  const handleRemoveImage = useCallback((fileInput: HTMLInputElement | null) => {
    setSelectedFile(null);
    setSelectedPreviewUrl((currentPreviewUrl) => {
      clearPreviewUrl(currentPreviewUrl);
      return null;
    });
    setRemoveCurrentImage(true);

    if (fileInput) {
      fileInput.value = '';
    }
  }, [clearPreviewUrl]);

  return {
    selectedFile,
    selectedPreviewUrl,
    removeCurrentImage,
    displayedImageUrl: selectedPreviewUrl || (removeCurrentImage ? null : currentImageUrl),
    handleFileChange,
    handleRemoveImage,
    resetDraft,
  };
};

export default useRealizationImageDraft;
